/**
 * Cliente HTTP de la mini-app de reclamos. Adjunta el token firmado del enlace
 * en cada llamada (header Authorization + query ?t= para el chat en vivo / SSE).
 * NO usa la cookie de sesión: es independiente del cliente global (api/client.ts).
 */
const BASE = '/api/claim-app';

export interface ClaimAppError extends Error {
  code?: string;
  status?: number;
}

function fail(message: string, code?: string, status?: number): ClaimAppError {
  return Object.assign(new Error(message), { code, status });
}

export function makeClaimClient(token: string) {
  const authHeaders = { Authorization: `Bearer ${token}` };

  async function get<T>(path: string): Promise<T> {
    const res = await fetch(`${BASE}${path}`, { headers: authHeaders });
    const j = await res.json().catch(() => ({}));
    if (!res.ok || !j.success) throw fail(j.error || 'error', j.error, res.status);
    return j as T;
  }

  async function post<T>(path: string, body?: unknown): Promise<T> {
    const res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { ...authHeaders, 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    const j = await res.json().catch(() => ({}));
    if (!res.ok || !j.success) throw fail(j.error || 'error', j.error, res.status);
    return j as T;
  }

  // Para SSE (POST streaming) el token viaja por query: fetch-stream no comparte
  // headers tan cómodamente y nginx ya está configurado para no bufferear.
  const sseUrl = (path: string) => `${BASE}${path}?t=${encodeURIComponent(token)}`;

  return { get, post, sseUrl, token };
}

export type ClaimClient = ReturnType<typeof makeClaimClient>;
